/**
 * Estimated arrival display:
 * - Before dispatch: order placed date (start of day) + estimated delivery days.
 * - After dispatch: dispatch date (start of day) + estimated delivery days.
 * Working days exclude Sundays (Royal Mail does not deliver on Sundays).
 */

import { addWorkingDays, startOfDay } from "./workingDays";

export function getEstimatedDeliveryDaysNumber(order) {
  const max = Number(order?.estimatedDeliveryMaxDays);
  const days = Number(order?.estimatedDeliveryDays);
  const n = Number.isFinite(max) && max >= 1 ? max : days;
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.min(30, Math.round(n));
}

export function getOrderPlacedAt(order) {
  const raw = order?.paidAt || order?.createdAt;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function getEstimatedDeliveryReferenceDate(order) {
  const raw = order?.dispatchedAt || getOrderPlacedAt(order);
  if (!raw) return null;
  const referenceDate = new Date(raw);
  if (Number.isNaN(referenceDate.getTime())) return null;
  referenceDate.setHours(0, 0, 0, 0);
  return referenceDate;
}

/** Formatted calendar ETA (before or after dispatch). */
export function getOrderEstimatedArrivalFormatted(order) {
  const days = getEstimatedDeliveryDaysNumber(order);
  if (days == null) return "";
  const referenceDate = getEstimatedDeliveryReferenceDate(order);
  if (!referenceDate) return "";
  const estDate = addWorkingDays(referenceDate, days);
  return estDate.toLocaleDateString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** User-facing ETA line (calendar date when calculable). */
export function getOrderEstimatedArrivalDisplay(order) {
  return getOrderEstimatedArrivalFormatted(order);
}

export function orderHasComputedEta(order) {
  return Boolean(getOrderEstimatedArrivalDisplay(order));
}

export function getOrderPlacedAtFormatted(order, options) {
  const d = getOrderPlacedAt(order);
  if (!d) return "";
  return d.toLocaleString(undefined, options);
}

export function getOrderSellerDispatchTimeDays(order) {
  const stored = Number(order?.sellerDispatchTimeDays);
  if (Number.isFinite(stored) && stored >= 1) {
    return Math.min(5, Math.max(1, Math.round(stored)));
  }
  let maxDispatch = 1;
  for (const item of order?.cart || []) {
    const raw = Number(item?.dispatchTimeDays);
    if (Number.isFinite(raw) && raw >= 1) {
      maxDispatch = Math.max(maxDispatch, Math.min(5, Math.round(raw)));
    }
  }
  return maxDispatch;
}

export function getSellerDispatchDeadline(order) {
  const dispatchDays = getOrderSellerDispatchTimeDays(order);
  const placed = getOrderPlacedAt(order);
  if (!placed) return null;
  return addWorkingDays(startOfDay(placed), dispatchDays);
}

export function getSellerDispatchDeadlineEnd(order) {
  const deadline = getSellerDispatchDeadline(order);
  if (!deadline) return null;
  const end = new Date(deadline);
  end.setHours(23, 59, 59, 999);
  return end;
}

export function isOrderCancelled(order) {
  if (!order) return false;
  const status = String(order.status || "").toLowerCase();
  if (status === "cancelled" || status === "canceled") return true;
  return order.buyerDispatchDelayChoice === "cancel";
}

export function isSellerDispatchOverdue(order, now = new Date()) {
  if (!order || isOrderCancelled(order)) return false;
  if (order.dispatchedAt || order.trackingStatus === "active") return false;
  const deadlineEnd = getSellerDispatchDeadlineEnd(order);
  if (!deadlineEnd) return false;
  return now.getTime() > deadlineEnd.getTime();
}

export function isSellerDispatchDueToday(order, now = new Date()) {
  if (!order || isOrderCancelled(order)) return false;
  if (order.dispatchedAt || order.trackingStatus === "active") return false;
  const deadline = getSellerDispatchDeadline(order);
  if (!deadline) return false;
  if (isSellerDispatchOverdue(order, now)) return false;
  return startOfDay(now).getTime() === startOfDay(deadline).getTime();
}

export function getSellerDispatchDeadlineFormatted(order) {
  const deadline = getSellerDispatchDeadlineEnd(order) || getSellerDispatchDeadline(order);
  if (!deadline) return "";
  return deadline.toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function buyerDispatchDelayChoicePending(order) {
  if (!order || isOrderCancelled(order)) return false;
  if (order.dispatchedAt || order.trackingStatus === "active") return false;
  if (!order.buyerDispatchDelayChoiceOfferedAt) return false;
  if (order.buyerDispatchDelayChoice) return false;
  return true;
}

export function buyerChoseToWaitForDispatch(order) {
  if (!order || isOrderCancelled(order)) return false;
  return order?.buyerDispatchDelayChoice === "wait";
}

export function buyerCancelledUndispatchedOrder(order) {
  if (!order) return false;
  return (
    order.buyerDispatchDelayChoice === "cancel" ||
    isOrderCancelled(order)
  );
}
